import { isNil } from '@lucidlink/tscore';
import { HTTPScheme, InternalError } from '#common/types';
import { getSettingFromList } from '#config';
import { ConfigValue, SettingName, } from '#config/types';
import { createSasConfigFetcher } from '#object-store/azure';
import { createStsConfigFetcher } from '#object-store/s3';
import { ObjectStoreCredentialsKind, ObjectStoreType, } from '#object-store/types';
export function buildObjectStoreConfig(settings, configManager) {
    const objectStoreType = getObjectStoreType(settings);
    const objectStoreConfig = getObjectStoreConfig(settings, objectStoreType);
    const buildParams = {
        type: objectStoreType,
        config: objectStoreConfig,
    };
    insertObjectStoreSasContext(buildParams, objectStoreType, configManager);
    insertObjectStoreStsContext(buildParams, objectStoreType, settings, configManager);
    return buildParams;
}
function getRequiredSetting(settings, name) {
    const setting = getSettingFromList(settings, name);
    if (isNil(setting)) {
        throw new InternalError(`Setting ${name} not found!`);
    }
    return setting;
}
function getObjectStoreConfig(settings, objectStoreType) {
    const protocol = getRequiredSetting(settings, SettingName.PROTOCOL);
    const provider = getRequiredSetting(settings, SettingName.PROVIDER);
    const objectStoreConfig = {
        httpScheme: getProtocolSetting(protocol),
        provider: provider.effective().asString(),
    };
    insertObjectStoreCredentials(settings, objectStoreType, objectStoreConfig);
    insertOptionalConfig(settings, objectStoreConfig);
    return objectStoreConfig;
}
function insertOptionalConfig(settings, objectStoreConfig) {
    const region = getRequiredSetting(settings, SettingName.REGION);
    const endpoint = getRequiredSetting(settings, SettingName.ENDPOINT);
    const locationConstraint = getRequiredSetting(settings, SettingName.LOCATION_CONSTRAINT);
    const virtualAddressing = getRequiredSetting(settings, SettingName.VIRTUAL_ADDRESSING);
    const storageClass = getRequiredSetting(settings, SettingName.STORAGE_CLASS);
    // in some cases the C++ daemon can write empty values for region and endpointOverride
    // we need to explicitly handle empty strings here and stop their propagation to avoid further confusion
    if (!isEmpty(region.effective().asString())) {
        objectStoreConfig.region = region.effective().asString();
    }
    if (!isEmpty(endpoint.effective().asString())) {
        objectStoreConfig.endpointOverride = endpoint.effective().asString();
    }
    objectStoreConfig.setLocationConstraint = locationConstraint.effective().asBoolean();
    objectStoreConfig.useVirtualAddressing = virtualAddressing.effective().asBoolean();
    objectStoreConfig.storageClass = storageClass.effective().asString();
}
function isEmpty(s) {
    return s === '';
}
function getObjectStoreType(settings) {
    const objectStoreType = getRequiredSetting(settings, SettingName.OBJECT_STORE);
    return objectStoreType.effective().asString();
}
function getProtocolSetting(s) {
    return s.effective().asString() === 'http' ? HTTPScheme.HTTP : HTTPScheme.HTTPS;
}
function insertObjectStoreCredentials(settings, objectStoreType, objectStoreConfig) {
    if (objectStoreType === ObjectStoreType.AZURE) {
        return;
    }
    const credType = getRequiredSetting(settings, SettingName.OBJECT_STORE_CREDENTIALS_TYPE).effective().asString();
    if (credType === ConfigValue.OBJECT_STORE_STS_CREDENTIALS) {
        objectStoreConfig.credentials = {
            kind: ObjectStoreCredentialsKind.CLIENT_STS,
        };
        return;
    }
    const accessKey = getRequiredSetting(settings, SettingName.ACCESS_KEY_ID).effective().asString();
    const secretKey = getRequiredSetting(settings, SettingName.SECRET_KEY).effective().asString();
    objectStoreConfig.credentials = {
        kind: ObjectStoreCredentialsKind.IAM,
        accessKey,
        secretKey,
    };
}
function insertObjectStoreSasContext(buildParams, objectStoreType, configManager) {
    if (objectStoreType !== ObjectStoreType.AZURE) {
        return;
    }
    buildParams.sasContext = {
        sasConfigFetcher: createSasConfigFetcher(configManager),
    };
}
function insertObjectStoreStsContext(buildParams, objectStoreType, settings, configManager) {
    if (objectStoreType !== ObjectStoreType.S3) {
        return;
    }
    const credType = getRequiredSetting(settings, SettingName.OBJECT_STORE_CREDENTIALS_TYPE).effective().asString();
    if (credType !== ConfigValue.OBJECT_STORE_STS_CREDENTIALS) {
        return;
    }
    buildParams.stsContext = {
        stsConfigFetcher: createStsConfigFetcher(configManager),
    };
}
